/* ==========================================================================
   PWR CORE
   The orchestrator. Loaded AFTER pwr-utils and pwr-events.
   Provides:
     - PWR.registerSection(type, factory): a controller registry that binds
       on DOM-ready AND on Shopify section:load, and tears down on
       section:unload. One pattern for every section — no bespoke listeners.
     - PWR.viewport: centralized breakpoint / scroll / resize / reduced-motion
       observation, broadcast on the PWR bus (one set of listeners for the
       whole theme instead of N).
     - Sticky header controller (data-pwr-sticky-header).
   Registers window.PWR.registerSection, window.PWR.viewport, window.PWR.Section.
   ========================================================================== */
(function () {
  'use strict';

  window.PWR = window.PWR || {};
  if (window.PWR.registerSection) return;

  const { utils, bus, EVENTS } = window.PWR;

  /* ==========================================================================
     BASE SECTION CONTROLLER
     Extend this for section behaviour; lifecycle is handled by the registry.
     ========================================================================== */
  class Section {
    constructor(root) {
      this.root = root;
      this.sectionId = root.getAttribute('data-section-id') || root.id;
      this._cleanups = [];
    }

    /** Register a teardown callback (listeners, observers, timers). */
    addCleanup(fn) {
      if (typeof fn === 'function') this._cleanups.push(fn);
    }

    /** Convenience: scoped querySelector within the section. */
    $(selector) { return utils.qs(selector, this.root); }
    $$(selector) { return utils.qsa(selector, this.root); }

    /** Override in subclasses. Called once when the section mounts. */
    init() {}

    /** Called on section:unload (Theme Editor) or manual destroy. */
    destroy() {
      this._cleanups.forEach((fn) => {
        try { fn(); } catch (e) { /* no-op */ }
      });
      this._cleanups = [];
    }
  }

  /* ==========================================================================
     SECTION REGISTRY
     ========================================================================== */
  const registry = new Map();      // type -> factory
  const instances = new WeakMap(); // root element -> controller instance

  /**
   * Register a controller for a section type.
   * @param {string} type  Value of data-section-type on the section root.
   * @param {(root: Element) => {init:Function, destroy?:Function}} factory
   */
  function registerSection(type, factory) {
    registry.set(type, factory);
    // Mount any instances already in the DOM (script may load after markup).
    mountType(type, document);
    return () => registry.delete(type);
  }

  function selectorFor(type) {
    return `[data-section-type="${type}"]`;
  }

  function mountOne(type, root) {
    if (instances.has(root)) return;
    const factory = registry.get(type);
    if (!factory) return;
    const instance = factory(root);
    instances.set(root, instance);
    if (instance && typeof instance.init === 'function') instance.init();
  }

  function mountType(type, scope) {
    utils.qsa(selectorFor(type), scope).forEach((root) => mountOne(type, root));
  }

  function unmount(root) {
    const instance = instances.get(root);
    if (instance && typeof instance.destroy === 'function') instance.destroy();
    instances.delete(root);
  }

  /* --- Lifecycle wiring via the PWR bus (bridged from Shopify events) --- */
  bus.on(EVENTS.sectionLoad, (detail) => {
    const target = detail.target;
    if (!target) return;
    // The loaded node may be the section root or a wrapper containing it.
    registry.forEach((_factory, type) => {
      if (target.matches && target.matches(selectorFor(type))) {
        mountOne(type, target);
      }
      mountType(type, target);
    });
  });

  bus.on(EVENTS.sectionUnload, (detail) => {
    const target = detail.target;
    if (!target) return;
    utils.qsa('[data-section-type]', target).forEach(unmount);
    if (target.matches && target.matches('[data-section-type]')) unmount(target);
  });

  // Initial mount pass for everything already registered.
  utils.ready(() => {
    registry.forEach((_factory, type) => mountType(type, document));
  });

  /* ==========================================================================
     VIEWPORT — centralized breakpoint / scroll / resize / motion
     ========================================================================== */
  const viewport = (function () {
    const mqDesktop = utils.mediaQuery(utils.BREAKPOINTS.md);
    const mqTablet = utils.mediaQuery(utils.BREAKPOINTS.sm);
    const mqMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const state = {
      scrollY: window.scrollY,
      lastScrollY: window.scrollY,
      direction: 'up',
      width: window.innerWidth,
      height: window.innerHeight,
      breakpoint: computeBreakpoint(),
      reducedMotion: mqMotion.matches,
    };

    function computeBreakpoint() {
      if (mqDesktop.matches) return 'desktop';
      if (mqTablet.matches) return 'tablet';
      return 'mobile';
    }

    const onScroll = utils.rafThrottle(() => {
      const y = window.scrollY;
      state.direction = y > state.lastScrollY ? 'down' : 'up';
      state.lastScrollY = y;
      state.scrollY = y;
      bus.emit(EVENTS.scroll, state);
    });

    const onResize = utils.debounce(() => {
      state.width = window.innerWidth;
      state.height = window.innerHeight;
      const next = computeBreakpoint();
      if (next !== state.breakpoint) {
        state.breakpoint = next;
        bus.emit(EVENTS.breakpointChange, { breakpoint: next });
      }
      bus.emit(EVENTS.resize, state);
    }, 150);

    function onMotionChange() {
      state.reducedMotion = mqMotion.matches;
      bus.emit(EVENTS.reducedMotionChange, { reducedMotion: state.reducedMotion });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    // Safari < 14 fallback for matchMedia listener.
    if (mqMotion.addEventListener) {
      mqMotion.addEventListener('change', onMotionChange);
    } else if (mqMotion.addListener) {
      mqMotion.addListener(onMotionChange);
    }

    return {
      get state() { return state; },
      isDesktop: () => state.breakpoint === 'desktop',
      isMobile: () => state.breakpoint === 'mobile',
      reducedMotion: () => state.reducedMotion,
    };
  })();

  /* ==========================================================================
     STICKY HEADER
     Progressive: hides on scroll-down, reveals on scroll-up, adds a
     "condensed" class past a threshold. Reduced-motion safe (no transitions
     added here; CSS owns the visual — JS only toggles state classes).
     Markup hook: <element data-pwr-sticky-header data-threshold="120">
     ========================================================================== */
  class StickyHeader {
    constructor(el) {
      this.el = el;
      this.threshold = parseInt(el.getAttribute('data-threshold'), 10) || 100;
      this.hideOnScroll = el.getAttribute('data-hide-on-scroll') !== 'false';
      this._unsub = null;
    }

    init() {
      this._unsub = bus.on(EVENTS.scroll, (s) => this.update(s));
      this.update(viewport.state);
    }

    update(s) {
      const condensed = s.scrollY > this.threshold;
      this.el.classList.toggle('is-condensed', condensed);

      if (!this.hideOnScroll) return;
      // Never hide while near the very top, or while a menu/drawer is open.
      const menuOpen = document.documentElement.classList.contains('pwr-no-scroll');
      const shouldHide = condensed && s.direction === 'down' && !menuOpen && s.scrollY > this.threshold * 2;
      this.el.classList.toggle('is-hidden', shouldHide);
    }

    destroy() {
      if (this._unsub) this._unsub();
    }
  }

  function initStickyHeaders(scope) {
    utils.qsa('[data-pwr-sticky-header]', scope || document).forEach((el) => {
      if (el._pwrSticky) return;
      const controller = new StickyHeader(el);
      controller.init();
      el._pwrSticky = controller;
    });
  }

  utils.ready(() => initStickyHeaders(document));
  bus.on(EVENTS.sectionLoad, (d) => d.target && initStickyHeaders(d.target));

  /* ==========================================================================
     SCROLL LOCK helper (used by drawers/modals; centralised to avoid dupes)
     ========================================================================== */
  let scrollLocks = 0;
  const scrollLock = {
    lock() {
      scrollLocks += 1;
      document.documentElement.classList.add('pwr-no-scroll');
    },
    unlock() {
      scrollLocks = Math.max(0, scrollLocks - 1);
      if (scrollLocks === 0) document.documentElement.classList.remove('pwr-no-scroll');
    },
  };

  /* -------------------------------------------------------------- EXPORTS */
  window.PWR.Section = Section;
  window.PWR.registerSection = registerSection;
  window.PWR.viewport = viewport;
  window.PWR.scrollLock = scrollLock;
  window.PWR.StickyHeader = StickyHeader;
})();
